import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import { styled } from 'styled-components'

import ArrayEditor from 'core/components/lib/ArrayEditor'
import { CardSectionSubmit } from 'core/components/lib/CardSection'
import Checkbox from 'core/components/lib/Checkbox'
import Clickable from 'core/components/lib/Clickable'
import JsonView from 'core/components/lib/JsonView'
import PeachIdLink from 'core/components/lib/PeachIdLink'
import RawJson from 'core/components/lib/RawJson'
import SegmentedControl from 'core/components/lib/SegmentedControl'
import { Select, MultiSelect } from 'core/components/lib/Select'
import { formatColumnHeader } from 'core/components/lib/Table/formatColumnHeader'
import Textarea from 'core/components/lib/Textarea'
import TextInput from 'core/components/lib/TextInput'
import { R } from 'core/helpers'
import { useForm } from 'core/hooks'
import {
  error,
  fontSizeLabel,
  fontSizeValue,
  fontSizeMeta,
  fontWeightMedium,
  radiusCard,
  spaceCardGap,
  spaceCardPadding,
} from 'core/styles'
import { useDarkModeInfo } from 'core/theme/DarkModeProvider'
import type { StaticFieldSchema } from 'core/utils/generated/formSchemas'
import { IMMUTABLE_FIELDS, isImmutableField } from 'core/utils/immutableFields'

import JsonTextarea from './JsonTextarea'
import ArrayValueView from './ArrayValueView'
import {
  controlGap,
  controlLineHeight,
  microGap,
  rowLineHeight,
  rowPaddingX,
  rowPaddingY,
  ViewModeText,
} from './common'

import {
  buildSelectOptions,
  getFormKey,
  normalizeSelectValueForForm,
  resolveComponentType,
  resolveNumberFormat,
} from './logic/fields'
import {
  formatUsPhoneInput,
  formatUserIdInput,
  isPhoneFieldName,
  toE164Us,
} from './logic/phone'
import {
  prepareInitialValues,
  pruneRequestBody,
  wrapOnSubmit,
  reconstructArrayFromFlat,
  updateArrayFlatValues,
} from './logic/formAdapter'
import { getValueAtPath } from 'core/utils/objectUtils'
import {
  formatViewValue,
  getFieldColorType,
  getViewModeColor,
} from './logic/format'
import {
  createMergedSchema,
  findSchemaField,
  generateRuntimeSchema,
  sortSchemaByKey,
  type SchemaField,
} from './logic/schema'
import { generateValidationRules } from './logic/validation'

// Stable, module-level defaults to avoid referential churn in hooks
const EMPTY_STRING_ARRAY: Array<string> = []
// Callers that pass no omit list still get the platform invariant.
const DEFAULT_OMIT_FIELDS: Array<string> = [...IMMUTABLE_FIELDS]

const collectReadOnlyPaths = (
  schema: Array<StaticFieldSchema & { readOnly?: boolean }>,
  parentPath?: string,
): Set<string> => {
  const paths = new Set<string>()

  for (const field of schema) {
    const path =
      field.fullPath ??
      (parentPath === undefined ? field.key : `${parentPath}.${field.key}`)

    if (field.readOnly === true) paths.add(path)
    for (const nested of collectReadOnlyPaths(field.nested ?? [], path)) {
      paths.add(nested)
    }
  }

  return paths
}

const EMPTY_FIELD_OVERRIDES: { [fieldPath: string]: SchemaField['component'] } =
  {}
const EMPTY_ARRAY_SCHEMAS: { [fieldPath: string]: Array<StaticFieldSchema> } =
  {}

const EMPTY_REQUIRED_OVERRIDES: { [fieldPath: string]: boolean } = {}

type DynamicFormProps<
  T extends { [key: string]: any } = { [key: string]: any },
> = {
  data: T
  onSubmit?: (data: T | Partial<T>) => Promise<void>
  validationRules?: { [path: string]: any }
  sections?: Array<string>
  fieldOverrides?: { [fieldPath: string]: SchemaField['component'] }
  schema?: Array<StaticFieldSchema> // Optional: generated schema for better type inference, falls back to runtime if missing
  /**
   * The fields an update accepts. Read mode shows the record; edit mode
   * renders this instead, so a field the API takes but the response never
   * returns is still reachable.
   */
  editSchema?: Array<StaticFieldSchema>
  arraySchemas?: { [fieldPath: string]: Array<StaticFieldSchema> }
  // Corrects a wrong `required` flag in the generated schema.
  // false drops the generated rule; true adds one the schema omits.
  requiredOverrides?: { [fieldPath: string]: boolean }
  selectOptions?: {
    [fieldPath: string]: Array<{
      label: string
      value: string
      sublabel?: string
    }>
  }
  fieldEditMode?: 'all' | 'viewOnly' | 'toggle'
  // Optional: actions to render to the left of the Edit button when in toggle mode
  otherActions?: Array<ReactNode>
  // Supports dot paths for nested fields, e.g. "parent.child".
  omitFields?: Array<string>
  // Optional: explicit ordering for TOP-LEVEL field names.
  fieldOrder?: Array<string>
  submitText?: string
  /**
   * Where Cancel goes in create mode. Toggle mode cancels back into the
   * record, so it ignores this and leaves edit mode instead.
   */
  onCancel?: () => void
  allowTabSwitchInAllMode?: boolean
  ignoreIsDirty?: boolean
  // Hides the Form/JSON/Raw control. Create pages have nothing to switch to.
  hideTabs?: boolean
}

const DynamicForm = <
  T extends { [key: string]: any } = { [key: string]: any },
>({
  data,
  onSubmit,
  validationRules,
  sections,
  fieldOverrides,
  schema,
  editSchema,
  arraySchemas,
  requiredOverrides,
  selectOptions,
  fieldEditMode = 'all',
  otherActions,
  omitFields,
  fieldOrder,
  submitText,
  onCancel,
  allowTabSwitchInAllMode = false,
  ignoreIsDirty = false,
  hideTabs = false,
}: DynamicFormProps<T>) => {
  const { darkMode } = useDarkModeInfo()
  const runtimeSchema = useMemo(() => generateRuntimeSchema(data), [data])
  const mergedSchema = useMemo(
    () => sortSchemaByKey(createMergedSchema(schema, runtimeSchema)),
    [schema, runtimeSchema],
  )

  // Built from the update body alone, with no runtime merge: the record's own
  // shape is what read mode shows, and mixing it in here would put fields the
  // API rejects back on the edit form.
  const editMergedSchema = useMemo(
    () =>
      editSchema === undefined
        ? undefined
        : sortSchemaByKey(createMergedSchema(editSchema, [])),
    [editSchema],
  )

  // Use fieldOverrides directly to avoid temporal dead zone issues
  const flattenedData = useMemo(
    () =>
      prepareInitialValues(
        data,
        // Seeds the write-only fields too, so they are registered with the
        // form. An untouched empty one is dropped from the body.
        editMergedSchema
          ? [...mergedSchema, ...editMergedSchema]
          : mergedSchema,
        fieldOverrides ?? EMPTY_FIELD_OVERRIDES,
      ),
    [data, mergedSchema, editMergedSchema, fieldOverrides],
  )

  const effectiveFieldOverrides = fieldOverrides ?? EMPTY_FIELD_OVERRIDES
  const effectiveArraySchemas = arraySchemas ?? EMPTY_ARRAY_SCHEMAS
  const effectiveRequiredOverrides =
    requiredOverrides ?? EMPTY_REQUIRED_OVERRIDES
  const effectiveOmitFields = omitFields ?? DEFAULT_OMIT_FIELDS
  const effectiveFieldOrder = fieldOrder ?? EMPTY_STRING_ARRAY

  const [isEditToggleOn, setIsEditToggleOn] = useState(false)
  const editActive =
    fieldEditMode === 'all' || (fieldEditMode === 'toggle' && isEditToggleOn)

  const activeSchema =
    editActive && editMergedSchema !== undefined
      ? editMergedSchema
      : mergedSchema
  const activeStaticSchema =
    editActive && editSchema !== undefined ? editSchema : schema

  // The spec's own statement. Kept beside the omit list so a caller that
  // passes an edit schema needs no second list to go with it.
  const readOnlyPaths = useMemo(
    () => collectReadOnlyPaths(activeStaticSchema ?? []),
    [activeStaticSchema],
  )

  // Every body the form sends, from Form or Raw, drops these paths.
  const submitOmitPaths = useMemo(
    () => [...effectiveOmitFields, ...readOnlyPaths],
    [effectiveOmitFields, readOnlyPaths],
  )

  const omitSet = useMemo(
    () => new Set(effectiveOmitFields),
    [effectiveOmitFields],
  )

  // The one test the render path, the initial values, and the submitted body
  // all share. Immutable fields hold even when the omit list does not name
  // them, because some update schemas declare them writable.
  const isOmitted = useCallback(
    (formKey: string) =>
      omitSet.has(formKey) ||
      isImmutableField(formKey) ||
      readOnlyPaths.has(formKey),
    [omitSet, readOnlyPaths],
  )

  // Derive a stable signature for omitFields content to avoid identity churn
  const omitFieldsSignature = useMemo(() => {
    if (effectiveOmitFields.length === 0) return ''
    const sorted = [...effectiveOmitFields].sort()
    return sorted.join('|')
  }, [effectiveOmitFields.join('|')])

  const initialValues = useMemo(() => {
    if (!editActive) return flattenedData
    const entries = Object.entries(flattenedData).filter(
      ([key]) => !isOmitted(key),
    )
    return Object.fromEntries(entries)
  }, [flattenedData, isOmitted, editActive])

  const form = useForm(initialValues, {
    onSubmit: async (flatValues) => {
      const isRawEditing =
        fieldEditMode === 'toggle' && isEditToggleOn && displayMode === 'raw'

      if (isRawEditing) {
        let parsed: any
        try {
          parsed = JSON.parse(rawEditString)
        } catch (_e: any) {
          setRawJsonError('Invalid JSON')
          return
        }
        await onSubmit?.(pruneRequestBody(parsed as any, submitOmitPaths) as T)
      } else {
        const mergedFlatValues = {
          ...flatValues,
          ...jsonInputValuesRef.current,
        }
        const convertedFlatValues: { [key: string]: any } = {
          ...mergedFlatValues,
        }
        for (const [k, v] of Object.entries(mergedFlatValues)) {
          if (isPhoneFieldName(k) && typeof v === 'string') {
            const e164 = toE164Us(v)
            if (e164 !== undefined) convertedFlatValues[k] = e164
          }
        }
        await wrapOnSubmit(
          data as unknown as { [key: string]: any },
          activeSchema,
          onSubmit,
          { omitPaths: submitOmitPaths },
        )(convertedFlatValues as { [key: string]: any })
      }

      if (fieldEditMode === 'toggle') {
        try {
          setIsEditToggleOn(false)
          setEditState((prev) => {
            const nextStates: { [k: string]: any } = {}
            for (const [k, st] of Object.entries(prev.fieldStates)) {
              nextStates[k] = {
                ...st,
                isEditing: false,
                hasError: false,
                errorMessage: undefined,
              }
            }
            return { currentEditingField: null, fieldStates: nextStates }
          })
          jsonInputValuesRef.current = {}
          setRawJsonError(null)
        } catch {}
      }
    },
    rules: {
      ...generateValidationRules(activeSchema, {
        requiredOverrides: effectiveRequiredOverrides,
        staticSchema: activeStaticSchema,
      }),
      ...(validationRules ?? {}),
    },
  })

  const [editState, setEditState] = useState({
    currentEditingField: null as string | null,
    fieldStates: {} as {
      [path: string]: {
        isEditing: boolean
        originalValue: unknown
        hasError: boolean
        errorMessage?: string
      }
    },
  })

  const [displayMode, setDisplayMode] = useState<'form' | 'json' | 'raw'>(
    'form',
  )
  const [rawEditString, setRawEditString] = useState<string>(() =>
    JSON.stringify(data, null, 2),
  )
  const [rawJsonError, setRawJsonError] = useState<string | null>(null)
  const isRawEditing =
    fieldEditMode === 'toggle' && isEditToggleOn && displayMode === 'raw'
  const rawIsValid = useMemo(() => {
    try {
      JSON.parse(rawEditString)
      return true
    } catch {
      return false
    }
  }, [rawEditString])
  const rawIsDirty = useMemo(() => {
    try {
      return rawEditString !== JSON.stringify(data, null, 2)
    } catch {
      return true
    }
  }, [rawEditString, data])

  const allPrimitiveFormKeys = useMemo(() => {
    const collectFormKeys = (
      fields: Array<SchemaField>,
      parentPath?: string,
    ): Array<string> => {
      const keys: Array<string> = []
      fields.forEach((f) => {
        const formKey = getFormKey(parentPath, f.key)
        if ((f.type === 'object' || f.type === 'array') && f.nested) {
          keys.push(...collectFormKeys(f.nested, formKey))
        } else {
          keys.push(formKey)
        }
      })
      return keys
    }
    return collectFormKeys(activeSchema)
  }, [activeSchema])

  // Guard to avoid repeated auto-start runs for the same state
  const autoStartSignatureRef = useRef<string | null>(null)

  useEffect(() => {
    const shouldEnableAll =
      fieldEditMode === 'all' || (fieldEditMode === 'toggle' && isEditToggleOn)
    if (!shouldEnableAll) return

    const allKeys = allPrimitiveFormKeys.filter((k) => !isOmitted(k))

    const signature = `${fieldEditMode}|${isEditToggleOn ? 1 : 0}|${
      allKeys.length
    }|${allKeys.join(',')}|${omitFieldsSignature}`
    if (autoStartSignatureRef.current === signature) return

    setEditState((prev) => {
      const next = { ...prev, fieldStates: { ...prev.fieldStates } }
      allKeys.forEach((k) => {
        if (!next.fieldStates[k]) {
          next.fieldStates[k] = {
            isEditing: true,
            originalValue: flattenedData[k],
            hasError: false,
          }
        } else {
          next.fieldStates[k] = { ...next.fieldStates[k], isEditing: true }
        }
      })
      // Do not force a single current field in multi/all mode
      next.currentEditingField = null
      return next
    })

    autoStartSignatureRef.current = signature
  }, [
    fieldEditMode,
    isEditToggleOn,
    allPrimitiveFormKeys,
    omitFieldsSignature,
    flattenedData,
    omitSet,
  ])

  const jsonInputValuesRef = useRef<{ [key: string]: unknown }>({})

  // Track JsonInput fields with invalid JSON to disable submit button
  const [invalidJsonFields, setInvalidJsonFields] = useState<Set<string>>(
    new Set(),
  )

  // The raw tab has to drop exactly what the form tab drops, or the two
  // submit different bodies for the same record.
  const pruneDefaultOmit = (obj: any) =>
    !obj || typeof obj !== 'object' || Array.isArray(obj)
      ? obj
      : pruneRequestBody(obj, submitOmitPaths)

  useEffect(() => {
    if (!isEditToggleOn) return
    if (displayMode === 'form' || displayMode === 'raw') {
      form.clear()
      jsonInputValuesRef.current = {}
      if (displayMode === 'raw') {
        const pruned = pruneDefaultOmit(data)
        setRawEditString(JSON.stringify(pruned, null, 2))
        setRawJsonError(null)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayMode, isEditToggleOn])

  const exitEditMode = () => {
    setIsEditToggleOn(false)
    form.setValues(initialValues)
  }

  const renderField = (field: SchemaField, parentPath?: string) => {
    const formKey = getFormKey(parentPath, field.key)

    if (editActive && isOmitted(formKey)) return null

    const schemaField = findSchemaField(activeStaticSchema, formKey)

    const fieldProps = form.fields?.[formKey]
    const fieldState = editState.fieldStates[formKey]
    const isEditing =
      fieldEditMode === 'all' || (fieldEditMode === 'toggle' && isEditToggleOn)

    const renderFieldInput = () => {
      if (!isEditing) {
        // For array roots, prefer the source data value because flattened form state
        // does not hold the aggregate array at the root key.
        const valueFromData = getValueAtPath(data, formKey)
        const isArrayField =
          schemaField?.type === 'array' ||
          field.type === 'array' ||
          field.component === 'ArrayEditor'
        const value = isArrayField ? valueFromData : fieldProps?.value
        const componentType = resolveComponentType({
          schemaField,
          fieldOverride: effectiveFieldOverrides[formKey],
          runtimeComponent: field.component,
        })

        if (componentType === 'JsonInput') {
          try {
            // Prefer original data value in view mode (root objects aren't stored in flattened form state)
            const jsonSource =
              valueFromData !== undefined ? valueFromData : fieldProps?.value
            const jsonString = JSON.stringify(jsonSource, null, 2)
            return <JsonViewModeText>{jsonString}</JsonViewModeText>
          } catch {
            return <ViewModeText $color='error'>Invalid JSON</ViewModeText>
          }
        }

        if (isArrayField && Array.isArray(value)) {
          return <ArrayValueView fieldPath={formKey} value={value} />
        }

        if (isPhoneFieldName(formKey)) {
          const raw = getValueAtPath(data, formKey)
          // An empty string formats to an empty string, so let it fall through
          // and render as '' like every other empty value.
          if (typeof raw === 'string' && raw.trim() !== '') {
            const formatted = formatUsPhoneInput(raw)
            return <ViewModeText>{formatted || raw}</ViewModeText>
          }
        }

        // Read-only values carry the JSON tab's syntax color per type, so a
        // number, string, or boolean reads the same on both tabs.
        const colorType = getFieldColorType(componentType, value, schemaField)
        const rendered = formatViewValue(value, colorType)
        return (
          <PeachIdLink fieldName={field.key} record={data} value={value}>
            <ViewModeText
              $color={getViewModeColor(colorType, darkMode === 'dark')}
            >
              {rendered}
            </ViewModeText>
          </PeachIdLink>
        )
      }

      // Use field override if available (highest priority), then schema component, then runtime component
      const componentType = resolveComponentType({
        schemaField,
        fieldOverride: effectiveFieldOverrides[formKey],
        runtimeComponent: field.component,
      })
      // If the actual value type is boolean/number, prefer appropriate input regardless of schema mismatch
      const actualValue = fieldProps?.value ?? getValueAtPath(data, formKey)
      const resolvedComponentType: typeof componentType =
        typeof actualValue === 'boolean'
          ? 'Checkbox'
          : typeof actualValue === 'number' && componentType === 'TextInput'
          ? 'NumberInput'
          : componentType

      switch (resolvedComponentType) {
        case 'Checkbox':
          return (
            <Checkbox
              name={formKey}
              {...(fieldProps as any)}
              value={fieldProps?.value ?? false}
            />
          )

        case 'Select': {
          // Prefer caller-provided selectOptions first (can include label/value/sublabel),
          // then schema options, then runtime field options
          const rawOptionsSource =
            selectOptions?.[formKey] ??
            schemaField?.options ??
            field.options ??
            []
          const options = Array.isArray(rawOptionsSource)
            ? typeof rawOptionsSource[0] === 'object'
              ? (rawOptionsSource as Array<{
                  label: string
                  value: string
                  sublabel?: string
                }>)
              : buildSelectOptions(rawOptionsSource as Array<string>)
            : []

          // Convert null/undefined to empty string for form handling (nullable enums)
          // React requires controlled inputs to have defined values
          const selectProps = {
            ...fieldProps,
            onChange: (value: any) => {
              fieldProps?.onChange?.(normalizeSelectValueForForm(value))
            },
            value:
              fieldProps?.value === null || fieldProps?.value === undefined
                ? ''
                : fieldProps?.value,
          }

          return (
            <Select
              menuPosition='fixed'
              name={formKey}
              options={options}
              {...selectProps}
            />
          )
        }

        case 'Textarea': {
          const originalValue = getValueAtPath(data, formKey)
          const wasNullish = R.isNil(originalValue)
          return (
            <Textarea
              rows={3}
              {...(fieldProps as any)}
              onChange={(value) => {
                // If user cleared the field and original was null/undefined, preserve null/undefined
                const nextValue =
                  value === '' && wasNullish ? originalValue : value
                fieldProps?.onChange?.(nextValue)
              }}
              value={fieldProps?.value ?? ''}
            />
          )
        }

        case 'JsonInput': {
          const jsonValue = fieldProps?.value ?? getValueAtPath(data, formKey)

          const handleJsonChange =
            fieldProps?.onChange ||
            ((value: unknown) => {
              jsonInputValuesRef.current[formKey] = value
              // Nudge form state so isDirty reflects JsonInput edits
              form.setValues({
                ...(form.formState.values as any),
                __jsonDirty: Date.now(),
              })
            })

          const handleJsonValidationChange = (isValid: boolean) => {
            setInvalidJsonFields((prev) => {
              const next = new Set(prev)
              if (isValid) {
                next.delete(formKey)
              } else {
                next.add(formKey)
              }
              return next
            })
          }

          return (
            <JsonTextarea
              onChange={handleJsonChange}
              onValidationChange={handleJsonValidationChange}
              placeholder={`Enter JSON for ${field.key}`}
              value={jsonValue}
            />
          )
        }

        case 'NumberInput': {
          const numberFormat = resolveNumberFormat(schemaField)
          return (
            <StyledTextInput
              format={numberFormat}
              name={formKey}
              variant='outline'
              {...(fieldProps as any)}
            />
          )
        }

        case 'ArrayEditor': {
          const candidate =
            effectiveArraySchemas[formKey] ||
            (schemaField?.nested as Array<StaticFieldSchema>) ||
            []
          // If the generator produced a wrapper node '0' for array items, unwrap to its nested fields
          const itemSchema =
            candidate.length === 1 &&
            candidate[0]?.key === '0' &&
            candidate[0]?.type === 'object' &&
            Array.isArray(candidate[0]?.nested)
              ? (candidate[0]?.nested as Array<StaticFieldSchema>)
              : candidate
          if (itemSchema.length === 0) {
            return (
              <StyledTextInput
                name={formKey}
                variant='outline'
                {...(fieldProps as any)}
              />
            )
          }

          const arrayFromFlat = reconstructArrayFromFlat(
            form.formState.values as { [key: string]: any },
            formKey,
          )
          const arrayFromData = getValueAtPath(data, formKey) as
            | Array<unknown>
            | undefined
          const arrayValue = arrayFromFlat ?? arrayFromData ?? []

          const handleArrayChange = (next: Array<unknown>) => {
            const currentFlat = form.formState.values as { [key: string]: any }
            const updatedFlat = updateArrayFlatValues(
              currentFlat,
              formKey,
              next,
            )
            form.setValues(updatedFlat)
          }

          return (
            <ArrayEditor
              itemSchema={itemSchema}
              name={formKey}
              onChange={handleArrayChange}
              readOnly={
                (fieldEditMode as 'all' | 'viewOnly' | 'toggle') ===
                  'viewOnly' ||
                (fieldEditMode === 'toggle' && !isEditToggleOn)
              }
              value={arrayValue}
            />
          )
        }

        case 'MultiSelect': {
          const rawOptions = selectOptions?.[formKey]
            ? (selectOptions?.[formKey] as Array<{
                label: string
                value: string
                sublabel?: string
              }>)
            : schemaField?.options ?? []
          const options = Array.isArray(rawOptions)
            ? typeof rawOptions[0] === 'object'
              ? (rawOptions as Array<any>)
              : buildSelectOptions(rawOptions as Array<string>)
            : []

          const arrayFromFlat = reconstructArrayFromFlat(
            form.formState.values as { [key: string]: any },
            formKey,
          )
          const arrayFromData = getValueAtPath(data, formKey) as
            | Array<unknown>
            | undefined
          const value = (arrayFromFlat ?? arrayFromData ?? []) as Array<string>

          const handleMultiChange = (next: Array<string> | null) => {
            const nextArray = next ?? []
            const currentFlat = form.formState.values as { [key: string]: any }
            const updatedFlat = updateArrayFlatValues(
              currentFlat,
              formKey,
              nextArray,
            )
            form.setValues(updatedFlat)
          }

          return (
            <MultiSelect
              menuPosition='fixed'
              name={formKey}
              onChange={handleMultiChange as any}
              options={options}
              value={value}
            />
          )
        }

        default: {
          const isPhone = isPhoneFieldName(formKey)
          const isUserId = formKey === 'userId'
          const originalValue = getValueAtPath(data, formKey)
          const wasNullish = R.isNil(originalValue)
          return (
            <StyledTextInput
              name={formKey}
              placeholder={
                isPhone
                  ? '(xxx) xxx - xxxx'
                  : isUserId
                  ? 'XX-YYYY-YYYY'
                  : undefined
              }
              variant='outline'
              {...fieldProps}
              onChange={(value) => {
                let next = value as string
                if (isPhone) {
                  next = formatUsPhoneInput(next)
                } else if (isUserId) {
                  next = formatUserIdInput(next)
                }
                // If user cleared the field and original was null/undefined, preserve null/undefined
                const nextValue =
                  next === '' && wasNullish ? originalValue : next
                fieldProps?.onChange?.(nextValue)
              }}
              value={
                isPhone
                  ? formatUsPhoneInput((fieldProps?.value as string) ?? '')
                  : isUserId
                  ? formatUserIdInput((fieldProps?.value as string) ?? '')
                  : fieldProps?.value ?? ''
              }
            />
          )
        }
      }
    }

    const rawValue = getValueAtPath(data, formKey)
    const itemCount = Array.isArray(rawValue) ? rawValue.length : null

    return (
      <FieldRow $hasNested={itemCount !== null} key={formKey}>
        <FieldName $editing={isEditing}>
          {formatColumnHeader(field.key)}
          {itemCount !== null && <ItemCount>({itemCount})</ItemCount>}
        </FieldName>
        <FieldValue
          $isToggle={fieldEditMode === 'toggle'}
          $isViewOnly={
            fieldEditMode === 'viewOnly' ||
            (fieldEditMode === 'toggle' && !isEditToggleOn)
          }
          className={isEditing ? 'editing' : undefined}
          onClick={undefined}
        >
          <div className='field-input'>{renderFieldInput()}</div>
          {fieldState?.hasError && fieldState.errorMessage && (
            <ErrorMessage>{fieldState.errorMessage}</ErrorMessage>
          )}
        </FieldValue>
      </FieldRow>
    )
  }

  // Rows only, with no grid container of their own, so that sibling groups
  // rendered into the same `TableContainer` share one label-column width.
  const renderRows = (fields: Array<SchemaField>, currentPath?: string) =>
    fields.map((field) => {
      // For arrays meant to be edited, render the field input (ArrayEditor / MultiSelect) instead of recursing
      if (field.type === 'array' && field.component === 'ArrayEditor') {
        return renderField(field, currentPath)
      }

      const objectFormKey = getFormKey(currentPath, field.key)

      // The nesting branch below never reaches renderField, so an omitted
      // object would otherwise render its whole subtree as editable rows.
      if (editActive && isOmitted(objectFormKey)) return null

      const isOverriddenToJson =
        effectiveFieldOverrides[objectFormKey] === 'JsonInput'

      if (
        field.type === 'object' &&
        field.nested &&
        field.nested.length > 0 &&
        !isOverriddenToJson
      ) {
        const newPath = currentPath ? `${currentPath}.${field.key}` : field.key
        return (
          <FieldRow $hasNested key={field.key}>
            <FieldName>{formatColumnHeader(field.key)}</FieldName>
            <NestedGroup>
              <TableContainer>
                {renderRows(field.nested, newPath)}
              </TableContainer>
            </NestedGroup>
          </FieldRow>
        )
      }

      return renderField(field, currentPath)
    })

  const filteredSchema = sections
    ? activeSchema.filter((field) => sections.includes(field.key))
    : activeSchema

  const orderedSchema = useMemo(() => {
    const pinnedOrder = [...DEFAULT_OMIT_FIELDS, ...(effectiveFieldOrder || [])]
    const seen = new Set<string>()
    const byKey: { [k: string]: SchemaField } = {}
    for (const f of filteredSchema) byKey[f.key] = f
    const prioritized: Array<SchemaField> = []
    for (const key of pinnedOrder) {
      if (seen.has(key)) continue
      if (byKey[key]) {
        prioritized.push(byKey[key])
        seen.add(key)
      }
    }
    const rest: Array<SchemaField> = filteredSchema.filter(
      (f) => !seen.has(f.key),
    )
    return [...prioritized, ...rest]
  }, [filteredSchema, effectiveFieldOrder])

  const hasInvalidJsonFields = invalidJsonFields.size > 0

  const isSubmitDisabled = isRawEditing
    ? form.formState.isSubmitting || !rawIsValid || !rawIsDirty
    : form.formState.isSubmitting ||
      !form.formState.isValid ||
      hasInvalidJsonFields ||
      (!ignoreIsDirty && !form.formState.isDirty)

  return (
    <Root>
      <form onSubmit={form.handleSubmit}>
        {hideTabs && fieldEditMode !== 'toggle' ? null : (
          <Header>
            {hideTabs ? null : (
              <SegmentedControl
                onChange={(value) =>
                  setDisplayMode(value as 'form' | 'json' | 'raw')
                }
                options={[
                  { label: 'Form', value: 'form' },
                  {
                    disabled: allowTabSwitchInAllMode
                      ? false
                      : isEditToggleOn || fieldEditMode === 'all',
                    label: 'JSON',
                    value: 'json',
                  },
                  {
                    disabled: allowTabSwitchInAllMode
                      ? false
                      : fieldEditMode === 'all',
                    label: 'Raw',
                    value: 'raw',
                  },
                ]}
                value={displayMode}
              />
            )}
            {fieldEditMode === 'toggle' ? (
              isEditToggleOn ? (
                <CardSectionSubmit
                  cancelText='Cancel'
                  disabled={isSubmitDisabled}
                  margin='0'
                  onCancel={exitEditMode}
                  onSubmit={form.handleSubmit}
                  submitText={submitText ?? 'Submit'}
                />
              ) : (
                <HeaderActions>
                  {(otherActions ?? []).map((node, idx) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={idx}>{node}</div>
                  ))}
                  <Clickable
                    disabled={!onSubmit}
                    onClick={() => {
                      setIsEditToggleOn(true)
                      if (displayMode === 'json') setDisplayMode('form')
                    }}
                    variant='primary'
                  >
                    Edit
                  </Clickable>
                </HeaderActions>
              )
            ) : null}
          </Header>
        )}
        <Container>
          {displayMode === 'json' ? (
            <PaddedContent>
              <JsonView value={data} />
            </PaddedContent>
          ) : displayMode === 'raw' ? (
            <PaddedContent>
              {isEditToggleOn ? (
                <div>
                  <JsonTextarea
                    onChange={(val) => {
                      if (typeof val === 'string') setRawEditString(val)
                      setRawJsonError(null)
                    }}
                    placeholder='Enter JSON'
                    stringMode
                    value={(() => {
                      try {
                        const parsed = JSON.parse(rawEditString)
                        return JSON.stringify(pruneDefaultOmit(parsed), null, 2)
                      } catch {
                        return rawEditString
                      }
                    })()}
                  />
                  {rawJsonError ? (
                    <ErrorMessage>{rawJsonError}</ErrorMessage>
                  ) : null}
                  {isRawEditing ? (
                    <NoteText>
                      Note: read-only fields ({IMMUTABLE_FIELDS.join(', ')}) are
                      ignored on submit, along with any field this resource
                      cannot update.
                    </NoteText>
                  ) : null}
                </div>
              ) : (
                <RawJson value={data} />
              )}
            </PaddedContent>
          ) : (
            <TableContainer>{renderRows(orderedSchema)}</TableContainer>
          )}
        </Container>

        {(fieldEditMode === 'all' ||
          (fieldEditMode === 'toggle' && isEditToggleOn)) && (
          <CardSectionSubmit
            cancelText='Cancel'
            disabled={isSubmitDisabled}
            onCancel={fieldEditMode === 'toggle' ? exitEditMode : onCancel}
            onSubmit={form.handleSubmit}
            submitText={submitText ?? 'Submit'}
          />
        )}
      </form>
    </Root>
  )
}

export default DynamicForm

const Root = styled.div`
  margin-bottom: ${spaceCardGap};
`

/* Matches the list view TableWrapper: the border hugs the rows, so a row
   divider ends on it instead of stopping short inside a padded card.
   The clip is why the selects above render with menuPosition='fixed'. */
const Container = styled.div`
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${radiusCard};
  background-color: ${(p) => p.theme.background};
  overflow: hidden;
`

/* Restores the inset that Container's padding used to give non-row content. */
const PaddedContent = styled.div`
  padding: ${spaceCardPadding};
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${controlGap};
`

const TableContainer = styled.div`
  display: grid;
  grid-template-columns: minmax(150px, max-content) 1fr;
`

const FieldRow = styled.div<{ $hasNested?: boolean }>`
  display: contents;
  font-size: ${fontSizeValue};

  &:last-child > * {
    border-bottom: none;
  }

  /* A row holding a nested group would otherwise paint its whole subtree,
     so only the innermost row under the pointer highlights. */
  &:hover > * {
    background-color: ${(p) =>
      p.$hasNested ? 'transparent' : p.theme.rowHover};
  }

  /* display: contents leaves no box to draw on, so the rule goes on the cells.
     Both grid columns are adjacent, so the two halves read as one line. */
  & > * {
    border-bottom: 1px solid ${(p) => p.theme.border};
  }
`

const FieldName = styled.div<{ $editing?: boolean }>`
  display: flex;
  align-items: flex-start;
  border-right: 1px solid ${(p) => p.theme.border};
  cursor: default;
  padding: ${rowPaddingY} ${rowPaddingX};
  min-height: 40px;
  line-height: ${(p) => (p.$editing ? controlLineHeight : rowLineHeight)};
  word-break: break-word;
  color: ${(p) => p.theme.labelText};
  font-size: ${fontSizeLabel};
  font-weight: ${fontWeightMedium};
`

/** Cardinality is what separates an array from an object at a glance. */
const ItemCount = styled.span`
  margin-left: ${microGap};
  color: ${(p) => p.theme.labelText};
  font-weight: ${fontWeightMedium};
`

const FieldValue = styled.div<{ $isViewOnly?: boolean; $isToggle?: boolean }>`
  display: flex;
  position: relative;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  padding: ${rowPaddingY} ${rowPaddingX};
  min-height: 40px;
  line-height: ${rowLineHeight};
  color: ${(p) => p.theme.text};

  &:hover > .edit-icon {
    opacity: 1;
  }

  .field-input {
    flex: 1 1 auto;

    /* Make text-like inputs full width but leave checkboxes/buttons alone */
    input,
    textarea,
    select {
      width: 100%;
    }
  }

  .edit-icon {
    transition: opacity 0.15s ease-in-out;
    opacity: 0;
    margin-left: auto;
    pointer-events: none;
  }
`

// No margins: a group keeps the same row rhythm as the fields around it.
const NestedGroup = styled.div`
  min-width: 0;
`

const ErrorMessage = styled.div`
  margin-top: ${microGap};
  color: ${error};
  font-size: ${fontSizeMeta};
`

const StyledTextInput = styled(TextInput)`
  background: ${(p) => p.theme.background};
`

// Right-aligned regardless of whether a Title sits beside it in the Header,
// with even spacing between however many action buttons are rendered.
const HeaderActions = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  margin-left: auto;
`

const JsonViewModeText = styled(ViewModeText)`
  white-space: pre-wrap;
  font-family: Inter, Heebo, sans-serif;
  font-size: ${fontSizeMeta};
`

const NoteText = styled(ViewModeText)`
  opacity: 0.75;
  font-size: 12px;
`
